import { omit, pick } from 'lodash';

import { Address, AddressFormValidation, Recipient } from 'api/api';

import { FieldID, MetaAddressFormFields } from '../types';

type SuggestionValidableFields = Pick<Recipient, FieldID>;
type SuggestionValidableKeys = keyof SuggestionValidableFields;
type ValidationConfig = { [key in keyof SuggestionValidableFields]: boolean };

const requiredFieldConfigFallback: ValidationConfig = {
  address_lines: true,
  apartment_number: false,
  city: true,
  country: true,
  name: true,
  postal_code: true,
  region: true,
  street: true,
  street_number: true,
};

const isValueInRange = (value: string, min: number = 0, max: number = Infinity) => {
  const len = value.length;

  return len >= min && len <= max;
};

const getValue = (value: SuggestionValidableFields[SuggestionValidableKeys]) =>
  Array.isArray(value) ? value[0] : value;

/**
 * We don't use required validation in current suggestion flow, but adding it just in case
 */
export const requiredValidator =
  (
    address: SuggestionValidableFields,
    fieldMap: Map<SuggestionValidableKeys, boolean>,
    config?: AddressFormValidation['required']
  ) =>
  (key: SuggestionValidableKeys) => {
    if (fieldMap.has(key)) {
      return;
    }

    if (!config) {
      if (requiredFieldConfigFallback[key] && !address[key]) {
        fieldMap.set(key, false);
        return;
      } else {
        fieldMap.set(key, true);
        return;
      }
    }

    const fieldGroupConfig = config.filter(cfg => cfg.fields.includes(key))[0];

    // config defined but not for this field - treat as optional
    if (!fieldGroupConfig) {
      fieldMap.set(key, true);
      return;
    }

    // no min value or the config block contains one field - it's always required
    if (!fieldGroupConfig?.min || fieldGroupConfig.fields.length === 1) {
      const value = getValue(address[key]);
      fieldMap.set(key, !!value);
      return;
    }

    const requiredFieldsGroup = pick(address, fieldGroupConfig.fields); // TODO check if there are undefined values inherited

    const emptyValues = new Set(['', undefined, [''], null]);
    const nomEmptyFieldsCounter = Object.values(requiredFieldsGroup).filter(
      x => !emptyValues.has(x)
    ).length;

    for (const key of Object.keys(requiredFieldsGroup) as Partial<SuggestionValidableKeys>[]) {
      fieldMap.set(key, nomEmptyFieldsCounter < fieldGroupConfig.min);
    }

    return;
  };

const lengthValidator =
  (
    address: SuggestionValidableFields,
    fieldMap: Map<SuggestionValidableKeys, boolean>,
    config?: AddressFormValidation['length']
  ) =>
  (key: SuggestionValidableKeys) => {
    // field was already checked as a part of another field group
    if (fieldMap.has(key)) {
      return;
    }

    const val = getValue(address[key]);

    const fieldGroupConfig = config?.filter(cfg => cfg.fields.includes(key))[0];
    if (!config || !fieldGroupConfig) {
      // no field value - mark as passing validation
      fieldMap.set(key, true);
      return;
    }

    if (!val) {
      // no field value - mark as passing validation
      fieldMap.set(key, true);
      return;
    }

    if (fieldGroupConfig.fields.length === 1) {
      // one field in config
      const { min, max } = fieldGroupConfig;

      const isPassing = isValueInRange(val, min, max);

      fieldMap.set(key, isPassing);
      return;
    }

    const lengthFieldsGroup = pick(address, fieldGroupConfig.fields);

    const concatenatedGroupValues = Object.values(lengthFieldsGroup)
      .flat() // address lines is string[]
      .join('');

    const inRange = isValueInRange(
      concatenatedGroupValues,
      fieldGroupConfig.min,
      fieldGroupConfig.max
    );

    for (const key of Object.keys(lengthFieldsGroup) as Partial<SuggestionValidableKeys>[]) {
      fieldMap.set(key, inRange);
    }

    return;
  };

const getValidators = (address: SuggestionValidableFields, config?: AddressFormValidation) => {
  // map tracking which fields have been checked and if they are passing validation or not
  const checkedPassingLengthFields = new Map<SuggestionValidableKeys, boolean>();

  return {
    lengthValidator: lengthValidator(address, checkedPassingLengthFields, config?.length),
    hasLengthErrors: (key: SuggestionValidableKeys) =>
      checkedPassingLengthFields.get(key) === false,
  };
};

export const processSuggestion = (
  address: MetaAddressFormFields,
  suggestion?: Address,
  config?: AddressFormValidation
) => {
  if (!suggestion) return false;

  const coumpundAddress = { ...address, ...suggestion };

  // omit internal fields that are not subject to validation
  const addressWithoutInternalFields = omit(coumpundAddress, [
    'coordinates',
    'vat_type',
    'phone_number_country',
    'po_box_id',
    'building_name',
  ]);

  const { lengthValidator, hasLengthErrors } = getValidators(coumpundAddress, config);

  for (const key of Object.keys(
    addressWithoutInternalFields
  ) as (keyof typeof addressWithoutInternalFields)[]) {
    lengthValidator(key);

    if (hasLengthErrors(key)) {
      return false;
    }
  }

  return true;
};
